import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { arrowRotateCwIcon, fileExcelIcon, xIcon, pencilIcon } from "@progress/kendo-svg-icons";
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartSeries, ChartSeriesItem, ChartTitle, ChartTooltip, ChartValueAxis, ChartValueAxisItem } from "@progress/kendo-react-charts";
import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent, GridNoRecords, GridToolbar } from "@progress/kendo-react-grid";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import dayjs from "dayjs";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import DateCell from "../../components/cells/DateCell";
import { GridLoadingPanel } from "../../components/GridLoadingPanel";
import useConfirm from "components/useConfirm";
import { fetchApi } from "../../services/api";
import { Title } from "../../utils/title";
import { CarrierNavBar } from "./CarrierNavBar";
import { JsonResponse } from "TypeGen/json-response";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import { Link } from "react-router-dom";
import { CreateCarrierScoreEventDialog } from "./CreateCarrierScoreEventDialog";
import { ExcelExport, ExcelExportColumn as ExportColumn } from "@progress/kendo-react-excel-export";
import { formatNumber } from "@progress/kendo-intl";
import { DataSourceRequestState, toDataSourceRequestString } from "@progress/kendo-data-query";
import { DropdownFilterCellIDName } from "components/cells/DropdownFilterCellIDName";
import { executionEventOptions, executionEventTypeValues, ExecutionEventOption } from "./Scorecard.constants";

type RouteComponentParams = {
  carrierId: string;
};

type CarrierScoreEvent = {
  CarrierScoreEventID: number;
  CreatedOn: string;
  EventType: number;
  EventTypeLabel: string;
  Description: string;
  ScoreChange: number;
  ScoreAfter: number;
  QuoteID?: number;
  OrderID?: number;
  OrderNumber?: number;
  IsUndone: boolean;
  AdjustedOn?: string;
  AdjustedByFullName?: string;
  DisputeStatus?: number;
  DisputeStatusLabel?: string;
};

type CarrierScoreHistoryPoint = {
  CarrierScoreEventID: number;
  CreatedOn: string;
  ScoreAfter: number;
};

type WeightLegendItem = {
  Label: string;
  Weight: number;
  ScoreChange: number;
};

type CarrierScorecardResponse = {
  CurrentScore: number;
  PercentileRank: number | null;
  ThirtyDayScoreChange: number;
  PickupOnTimePercent: number | null;
  DeliveryOnTimePercent: number | null;
  ScoreHistory: CarrierScoreHistoryPoint[];
  WeightLegend: WeightLegendItem[];
};

type CarrierScoreEventsResponse = {
  Data: CarrierScoreEvent[];
  EventTypes: IDNameViewModel[];
  DisputeStatuses: IDNameViewModel[];
  Total: number;
};

const ScoreChangeCell = (props: GridCustomCellProps): ReactNode => {
  const value = props.dataItem[props.field as string];
  if (value === null || value === undefined) {
    return <td {...props.tdProps} />;
  }

  const numericValue = Number(value);
  const classNames = [
    props.tdProps.className,
    numericValue > 0 ? "text-success" : numericValue < 0 ? "text-danger" : "text-muted"
  ]
    .filter(Boolean)
    .join(" ");

  const formatted = numericValue > 0
    ? `+${numericValue.toFixed(2)}`
    : numericValue < 0
      ? numericValue.toFixed(2)
      : "0.00";

  return (
    <td {...props.tdProps} className={classNames}>
      {formatted}
    </td>
  );
};

const ScoreValueCell = (props: GridCustomCellProps): ReactNode => {
  const value = props.dataItem[props.field as string];
  if (value === null || value === undefined) {
    return <td {...props.tdProps} />;
  }

  return (
    <td {...props.tdProps}>
      {Math.round(value).toLocaleString()}
    </td>
  );
};

const EventTypeLabelCell = (props: GridCustomCellProps): ReactNode => {
  const scoreEvent = props.dataItem as CarrierScoreEvent;

  if (scoreEvent.OrderID) {
    return (
      <td {...props.tdProps}>
        <Link style={{ color: "#007bff" }} to={`/Order/${scoreEvent.OrderID}`}>
          {scoreEvent.EventTypeLabel}
        </Link>
      </td>
    );
  }

  if (scoreEvent.QuoteID) {
    return (
      <td {...props.tdProps}>
        <Link style={{ color: "#007bff" }} to={`/Quote/Index/${scoreEvent.QuoteID}`}>
          {scoreEvent.EventTypeLabel}
        </Link>
      </td>
    );
  }

  return (
    <td {...props.tdProps}>
      {scoreEvent.EventTypeLabel}
    </td>
  );
};

const formatScore = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }
  return Math.round(value).toLocaleString();
};

const formatPercentile = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }
  return `${value.toFixed(1)}%`;
};

const formatRatioPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return formatNumber(value, "p1");
};

const formatScoreDelta = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }

  const rounded = Math.round(value);
  if (rounded > 0) {
    return `+${rounded.toLocaleString()}`;
  }

  if (rounded < 0) {
    return rounded.toLocaleString();
  }

  return "0";
};

const getPercentileProgress = (percentile: number | null | undefined) => {
  if (percentile === null || percentile === undefined) {
    return 0;
  }

  return Math.max(0, Math.min(1, percentile / 100));
};

const CarrierScorecard = () => {
  const _excelExport = useRef<ExcelExport | null>(null);
  const { carrierId: carrierIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [data, setData] = useState<CarrierScorecardResponse | null>(null);
  const [eventsData, setEventsData] = useState<CarrierScoreEventsResponse>({ Data: [], EventTypes: [], DisputeStatuses: [], Total: 0 });
  const [undoingEventId, setUndoingEventId] = useState<number | null>(null);
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [editingEventType, setEditingEventType] = useState<number | null>(null);
  const [editingEventOriginalType, setEditingEventOriginalType] = useState<number | null>(null);
  const [savingEventType, setSavingEventType] = useState(false);
  const [showWeightLegend, setShowWeightLegend] = useState(false);
  const [showCreateEventDialog, setShowCreateEventDialog] = useState(false);
  const [eventsDataState, setEventsDataState] = useState<DataSourceRequestState>({
    skip: 0,
    take: 25,
    filter: { logic: "and", filters: [] }
  });
  const { ConfirmationDialog, confirm } = useConfirm({ labels: { confirm: "Remove", cancel: "Cancel" }, themeColor: "error" });

  const excelExport = () => {
    if (_excelExport.current)
    {
      _excelExport.current.save(eventsData.Data.map(event => ({
        ...event,
        CreatedOnExport: dayjs.utc(event.CreatedOn).toDate(),
        AdjustedOnExport: event.AdjustedOn ? dayjs.utc(event.AdjustedOn).toDate() : null,
        IsUndoneLabel: event.IsUndone ? "Yes" : "No"
      })));
    }
  };

  const refresh = useCallback(() => {
    setLoading(true);
    return fetchApi(`/api/Asset/CarrierScorecard/${carrierIdParam}`, {}, "POST")
      .then((response: CarrierScorecardResponse) => {
        setData(response);
      })
      .catch(err => {
        alert(err);
      })
      .finally(() => setLoading(false));
  }, [carrierIdParam]);

  const refreshEvents = useCallback(() => {
    setEventsLoading(true);
    const queryStr = toDataSourceRequestString(eventsDataState);

    return fetchApi(`/api/Asset/CarrierScoreEvents/${carrierIdParam}?${queryStr}`, {}, "POST")
      .then((response: CarrierScoreEventsResponse) => {
        setEventsData(response);
      })
      .catch(err => {
        alert(err);
      })
      .finally(() => setEventsLoading(false));
  }, [carrierIdParam, eventsDataState]);

  const refreshAll = useCallback(() => {
    refresh();
    refreshEvents();
  }, [refresh, refreshEvents]);

  const openEditEventTypeDialog = useCallback((scoreEvent: CarrierScoreEvent) => {
    if (!executionEventTypeValues.has(scoreEvent.EventType)) {
      return;
    }

    setEditingEventId(scoreEvent.CarrierScoreEventID);
    setEditingEventType(scoreEvent.EventType);
    setEditingEventOriginalType(scoreEvent.EventType);
  }, []);

  const closeEditEventTypeDialog = useCallback(() => {
    setEditingEventId(null);
    setEditingEventType(null);
    setEditingEventOriginalType(null);
  }, []);

  const handleEditEventTypeChange = useCallback((event: DropDownListChangeEvent) => {
    const option = event.value as ExecutionEventOption | null;
    setEditingEventType(option ? option.value : null);
  }, []);

  const handleUndo = useCallback(async (eventId: number) => {
    if (!(await confirm("Are you sure you want to remove this score event? This action cannot be undone."))) {
      return;
    }

    setUndoingEventId(eventId);
    fetchApi("/api/Asset/UndoCarrierScoreEvent", { CarrierScoreEventID: eventId }, "post")
      .then((response: JsonResponse) => {
        if (response.Success) {
          closeEditEventTypeDialog();
          refreshAll();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        if (!e?.status) alert("Unable to remove score event");
      })
      .finally(() => setUndoingEventId(null));
  }, [confirm, refreshAll, closeEditEventTypeDialog]);

  const handleSaveEventType = useCallback(() => {
    if (editingEventId === null || editingEventType === null) {
      return;
    }

    setSavingEventType(true);
    fetchApi("/api/Asset/UpdateCarrierScoreEventType", { CarrierScoreEventID: editingEventId, EventType: editingEventType }, "post")
      .then((response: JsonResponse) => {
        if (response.Success) {
          closeEditEventTypeDialog();
          refreshAll();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        if (!e?.status) alert("Unable to update score event");
      })
      .finally(() => setSavingEventType(false));
  }, [editingEventId, editingEventType, refreshAll, closeEditEventTypeDialog]);

  const renderActionButtonCell = useCallback((cellProps: GridCustomCellProps): ReactNode => {
    const event = cellProps.dataItem as CarrierScoreEvent;
    const isEditable = executionEventTypeValues.has(event.EventType) && !event.IsUndone;
    const isProcessing = undoingEventId === event.CarrierScoreEventID;

    return (
      <td {...cellProps.tdProps}>
        {isEditable ? <Button
          size="small"
          svgIcon={pencilIcon}
          onClick={() => openEditEventTypeDialog(event)}
        /> : <Button
          size="small"
          themeColor="error"
          svgIcon={xIcon}
          disabled={isProcessing || event.IsUndone}
          onClick={() => handleUndo(event.CarrierScoreEventID)}
        />}
      </td>
    );
  }, [handleUndo, undoingEventId, openEditEventTypeDialog]);

  const selectedExecutionEventOption = editingEventType !== null
    ? executionEventOptions.find(option => option.value === editingEventType) ?? null
    : null;

  const isSaveEventTypeDisabled = editingEventType === null || editingEventOriginalType === editingEventType || savingEventType;

  useEffect(() => {
    const handle = window.setTimeout(() => refresh(), 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  useEffect(() => {
    const handle = window.setTimeout(() => refreshEvents(), 0);
    return () => window.clearTimeout(handle);
  }, [refreshEvents]);

  const onEventsDataStateChange = (event: GridDataStateChangeEvent) => {
    setEventsDataState(event.dataState);
  };

  const scoreHistoryCategories = useMemo(
    () => (data?.ScoreHistory ?? [])
      .map(point => dayjs.utc(point.CreatedOn).toDate()),
    [data?.ScoreHistory]
  );

  const scoreHistoryValues = useMemo(
    () => (data?.ScoreHistory ?? []).map(point => Number(point.ScoreAfter.toFixed(2))),
    [data?.ScoreHistory]
  );

  return (
    <>
      <CarrierNavBar id={carrierIdParam} currentTab="Scorecard" />
      <Title string="Scorecard" />
      <ConfirmationDialog />
      <br />
      {loading && <GridLoadingPanel />}
      {!loading && !data && (
        <div className="text-center mt-5">
          <Loader type="converging-spinner" />
        </div>
      )}
      {!loading && data && (
        <>
          <div className="row">
            <div className="col-lg-4 col-md-6 mb-3 carrier-scorecard-summary-col">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="text-uppercase text-muted mb-2">Current Score</h6>
                  <div className="display-4">{formatScore(data.CurrentScore)}</div>
                  <small className="text-muted d-block mt-1">
                    Baseline score is 1,000. Scores are recalculated after every event.
                  </small>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3 carrier-scorecard-summary-col">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="text-uppercase text-muted mb-2">30-Day Score Change</h6>
                  <div className="display-4">{formatScoreDelta(data.ThirtyDayScoreChange)}</div>
                  <small className="text-muted d-block mt-1">
                    Change in score compared with 30 days ago.
                  </small>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3 carrier-scorecard-summary-col">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="text-uppercase text-muted mb-2">On Time Pickup</h6>
                  <div className="display-4">{formatRatioPercent(data.PickupOnTimePercent)}</div>
                  <small className="text-muted d-block mt-1">
                    Based on last 90 days.
                  </small>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3 carrier-scorecard-summary-col">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="text-uppercase text-muted mb-2">On Time Delivery</h6>
                  <div className="display-4">{formatRatioPercent(data.DeliveryOnTimePercent)}</div>
                  <small className="text-muted d-block mt-1">
                    Based on last 90 days.
                  </small>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3 carrier-scorecard-summary-col">
              <div className="card h-100">
                <div className="card-body d-flex flex-column">
                  <h6 className="text-uppercase text-muted mb-2">Percentile Ranking</h6>
                  <div className="display-4">{formatPercentile(data.PercentileRank)}</div>
                  <small className="text-muted d-block mt-1">
                    Rank versus all active carriers.
                  </small>
                  <div className="mt-auto rank-stripe-wrapper">
                    <div className="rank-stripe-fill" style={{ width: `${getPercentileProgress(data.PercentileRank) * 100}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(data.ScoreHistory?.length ?? 0) > 1 && <>
            <Chart transitions={false}>
              <ChartTitle text="Score Trend" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem categories={scoreHistoryCategories} type="date" baseUnit="weeks" />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem narrowRange={true} />
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem type="line" data={scoreHistoryValues} markers={{ visible: true }} />
              </ChartSeries>
              <ChartTooltip format="{0:n0}" />
            </Chart>
            <br />
          </>}

          <ExcelExport ref={_excelExport} fileName="Carrier Score Events.xlsx">
            <ExportColumn field="CreatedOnExport" title="Recorded" width={100} />
            <ExportColumn field="QuoteID" title="Quote ID" cellOptions={{ format: "0" }} />
            <ExportColumn field="OrderNumber" title="Order Number" cellOptions={{ format: "0" }} />
            <ExportColumn field="EventTypeLabel" title="Event" />
            <ExportColumn field="Description" title="Description" />
            <ExportColumn field="ScoreChange" title="Change" cellOptions={{ format: "+0.00;-0.00;0.00" }} />
            <ExportColumn field="ScoreAfter" title="Score" cellOptions={{ format: "#,##0" }} />
            <ExportColumn field="DisputeStatusLabel" title="Dispute" />
            <ExportColumn field="AdjustedByFullName" title="Adjusted By" />
            <ExportColumn field="AdjustedOnExport" title="Adjusted On" />
            <ExportColumn field="IsUndoneLabel" title="Undone" />
          </ExcelExport>

          {eventsLoading && <GridLoadingPanel />}
          <Grid
            {...eventsDataState}
            data={eventsData.Data}
            dataItemKey="CarrierScoreEventID"
            style={{ maxHeight: 449 }}
            total={eventsData.Total}
            onDataStateChange={onEventsDataStateChange}
            filterable
            pageable={{ pageSizes: [25, 50, 100] }}
            className="score-events-grid"
            rows={{ data: (rowProps) => <tr {...rowProps.trProps} style={rowProps.dataItem.IsUndone ? { opacity: "0.5" } : {}}>{rowProps.children}</tr> }}
          >
            <GridToolbar>
              <Button icon="refresh" svgIcon={arrowRotateCwIcon} title="Refresh" onClick={refreshAll} />
              <Button onClick={() => setShowWeightLegend(true)}>Legend</Button>
              <Button svgIcon={fileExcelIcon} onClick={excelExport}>Excel</Button>
              <Button themeColor="primary" onClick={() => setShowCreateEventDialog(true)}>+ Create Event</Button>
            </GridToolbar>
            <Column field="CreatedOn" title="Recorded" filter="date" cells={{ data: DateCell }} />
            <Column
              field="EventType"
              title="Event"
              filter="numeric"
              cells={{
                data: EventTypeLabelCell,
                filterCell: (props) => <DropdownFilterCellIDName {...props} data={eventsData.EventTypes} />
              }}
            />
            <Column field="Description" title="Description" />
            <Column field="ScoreChange" title="Change" filterable={false} cells={{ data: ScoreChangeCell }} />
            <Column field="ScoreAfter" title="Score" filterable={false} cells={{ data: ScoreValueCell }} />
            <Column
              field="DisputeStatus"
              title="Dispute"
              filter="numeric"
              cells={{
                data: (props: GridCustomCellProps) => <td {...props.tdProps}>{(props.dataItem as CarrierScoreEvent).DisputeStatusLabel}</td>,
                filterCell: (props) => <DropdownFilterCellIDName {...props} data={eventsData.DisputeStatuses} />
              }}
            />
            <Column field="AdjustedByFullName" title="Adjusted By" filterable={false} cells={{
              data: (props: GridCustomCellProps) => {
                const scoreEvent = props.dataItem as CarrierScoreEvent;
                if (!scoreEvent.AdjustedOn) {
                  return <td {...props.tdProps} />;
                }
                return (
                  <td {...props.tdProps} title={dayjs.utc(scoreEvent.AdjustedOn).tz("America/New_York").format("MM/DD/YYYY HH:mm")}>
                    {scoreEvent.AdjustedByFullName}
                  </td>
                );
              }
            }} />
            <Column title="" width={50} filterable={false} cells={{ data: renderActionButtonCell }} />
            <GridNoRecords>
              No score events recorded yet.
            </GridNoRecords>
          </Grid>
        </>
      )}

      {editingEventId !== null && (
        <Dialog title="Edit Score Event" onClose={() => !savingEventType && closeEditEventTypeDialog()} className="dialog-w525">
          <p className="mb-3">
            Updating type will recalculate the carrier's score from this point forward.
          </p>
          <DropDownList
            data={executionEventOptions}
            textField="label"
            dataItemKey="value"
            value={selectedExecutionEventOption}
            onChange={handleEditEventTypeChange}
            style={{ width: "100%" }}
          />
          <DialogActionsBar layout="end">
            <Button onClick={closeEditEventTypeDialog} disabled={savingEventType}>Cancel</Button>
            <Button themeColor="error" onClick={() => handleUndo(editingEventId)} disabled={savingEventType}>Remove</Button>
            <Button themeColor="primary" onClick={handleSaveEventType} disabled={isSaveEventTypeDisabled}>Update</Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {showWeightLegend && data && (
        <Dialog title="Score Event Legend" onClose={() => setShowWeightLegend(false)} className="dialog-w525">
          <p>The following score event weights are used to calculate your carrier score. Scores are 2x for the first 60 events.</p>
          <div className="mb-3">
            {data.WeightLegend.map(item => (
              <div key={item.Label} className="mb-2">
                <span className={item.ScoreChange > 0 ? "text-success" : item.ScoreChange < 0 ? "text-danger" : "text-muted"}>
                  {item.ScoreChange > 0 ? "+" : ""}{item.ScoreChange.toFixed(2)}
                </span>: {item.Label}
              </div>
            ))}
          </div>
          <DialogActionsBar layout="end">
            <Button onClick={() => setShowWeightLegend(false)}>Close</Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {showCreateEventDialog && (
        <CreateCarrierScoreEventDialog
          carrierId={carrierIdParam}
          onClose={() => setShowCreateEventDialog(false)}
          onCreated={refreshAll}
        />
      )}
    </>
  );
};

export default CarrierScorecard;
